import React, { useState, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TextField, TextAreaField, CheckboxField, CheckboxGroupField, MultiSelectField as UIMultiSelectField, ComboboxField } from "./ui/FormControls";
import { ImageDropField } from "./ui/ImageDropField";
import {
  fetchDistrictsByState,
  fetchTalukasByDistrict,
  fetchSubcategoriesByCategory,
  fetchServicesByCategory,
} from "../store/slices/commonApiSlice";

// Validation schemas for each modal type
const contactSchema = yup.object({
  contact_numbers: yup
    .array()
    .of(yup.string().max(10, "Contact number must be max 10 digits"))
    .nullable(),
  whatsapp: yup
    .string()
    .nullable()
    .max(10, "WhatsApp number must be max 10 digits"),
  email: yup.string().nullable().email("Enter a valid email"),
  website: yup.string().nullable().url("Enter a valid URL"),
});

const discountSchema = yup.object({
  discount_text: yup.string().max(255, "Discount text must be less than 255 characters").nullable(),
  fixed_discount: yup.number().min(0, "Must be 0 or more").nullable(),
  customer_discount: yup.number().min(0, "Must be 0 or more").nullable(),
  company_commission: yup.number().min(0, "Must be 0 or more").nullable(),
});



const gallerySchema = yup.object({
  gallery: yup.mixed().nullable(),
  remove_images: yup.array().of(yup.string()).nullable(),
});

const businessInfoSchema = yup.object({
  business_name: yup.string().required("Business name is required").max(20, "Business name cannot exceed 20 characters"),
  about_business: yup.string().max(255, "About business must be less than 255 characters").nullable(),
  username: yup
    .string()
    .required("Mobile number is required")
    .matches(/^[0-9]+$/, "Mobile number must contain only digits")
    .min(10, "Mobile number must be exactly 10 digits")
    .max(10, "Mobile number must be exactly 10 digits"),
  is_trending: yup.boolean(),
  is_recently_added: yup.boolean(),
});

const businessSettingsSchema = yup.object({
  subscription_package_id: yup.string().nullable(),
  payment_modes: yup.array().of(yup.string()).nullable(),
  business_timings: yup.object().nullable(),
});

const locationSchema = yup.object({
  state_id: yup.string().nullable(),
  district_id: yup.string().nullable(),
  taluka_id: yup.string().nullable(),
  address: yup.string().max(500, "Address must be less than 500 characters").nullable(),
  pincode: yup.string().max(6, "Pincode must be max 6 digits").nullable(),
  business_location_map: yup.string().url("Enter a valid URL").nullable(),
  latitude: yup.number().transform((value, originalValue) => {
    return originalValue === '' ? null : value;
  }).min(-90).max(90).nullable(),
  longitude: yup.number().transform((value, originalValue) => {
    return originalValue === '' ? null : value;
  }).min(-180).max(180).nullable(),
});

const categoriesServicesSchema = yup.object({
  category_id: yup.string().nullable(),
  sub_categories: yup.array().of(yup.string()).nullable(),
  services: yup.array().of(yup.string()).nullable(),
});

// Contact Edit Modal Form
export const ContactEditForm = ({ data, onSave, loading = false }) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      contact_numbers: Array.isArray(data?.contact_numbers)
        ? data.contact_numbers.filter(Boolean)
        : [data?.contact_numbers || ""].filter(Boolean).length > 0
        ? [data?.contact_numbers]
        : [""],
      email: data?.email || "",
      website: data?.website || "",
      whatsapp: data?.whatsapp_number || data?.whatsapp || "",
    },
  });

  // Reset form when data changes
  useEffect(() => {
    if (data) {
      reset({
        contact_numbers: Array.isArray(data?.contact_numbers)
          ? data.contact_numbers.filter(Boolean)
          : [data?.contact_numbers || ""].filter(Boolean).length > 0
          ? [data?.contact_numbers]
          : [""],
        email: data?.email || "",
        website: data?.website || "",
        whatsapp: data?.whatsapp_number || data?.whatsapp || "",
      });
    }
  }, [data, reset]);

  const {
    fields: contactNumbers,
    append: appendContactNumber,
    remove: removeContactNumber,
  } = useFieldArray({
    name: "contact_numbers",
    control,
  });

  useEffect(() => {
    if (contactNumbers.length === 0) {
      appendContactNumber("");
    }
  }, [contactNumbers.length, appendContactNumber]);

  const onSubmit = (formData) => {
    // Clean and validate contact numbers
    const cleanedData = {};
    
    if (formData.contact_numbers && formData.contact_numbers.length > 0) {
      const filteredNumbers = formData.contact_numbers.filter(num => num && num.trim() !== '');
      if (filteredNumbers.length > 0) {
        cleanedData.contact_numbers = filteredNumbers;
      }
    }
    
    if (formData.whatsapp && formData.whatsapp.trim() !== '') {
      cleanedData.whatsapp = formData.whatsapp;
    }
    
    if (formData.email && formData.email.trim() !== '') {
      cleanedData.email = formData.email;
    }
    
    if (formData.website && formData.website.trim() !== '') {
      cleanedData.website = formData.website;
    }
    
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Contact Information</h3>
        <p className="text-sm text-gray-500 mt-1">Update business contact details</p>
      </div>
      
      {/* Contact Numbers */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Contact Numbers <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={() => appendContactNumber("")}
            className="text-blue-600 text-sm hover:text-blue-800"
          >
            + Add Number
          </button>
        </div>
        <div className="space-y-2">
          {contactNumbers.map((field, index) => (
            <div key={field.id} className="flex gap-2">
              <TextField
                placeholder="10-digit number"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                {...register(`contact_numbers.${index}`)}
                error={errors?.contact_numbers?.[index]?.message}
              />
              {contactNumbers.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeContactNumber(index)}
                  className="text-red-600 text-sm hover:text-red-800 px-3 py-2 border border-red-300 rounded"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
        {errors?.contact_numbers && (
          <p className="text-red-500 text-sm mt-1">
            {errors.contact_numbers.message}
          </p>
        )}
      </div>

      {/* Other fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label="WhatsApp Number"
          placeholder="10-digit number"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={10}
          {...register("whatsapp")}
          error={errors?.whatsapp?.message}
        />

        <TextField
          label="Email"
          placeholder="example@domain.com"
          {...register("email")}
          error={errors?.email?.message}
        />

        <TextField
          label="Website"
          placeholder="https://example.com"
          {...register("website")}
          error={errors?.website?.message}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

// Discount Edit Modal Form
export const DiscountEditForm = ({ data, onSave, loading = false }) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(discountSchema),
    defaultValues: {
      discount_text: data?.active_discount?.discount_text || "",
      fixed_discount: data?.active_discount?.fixed_discount || "",
      customer_discount: data?.active_discount?.customer_discount || "",
      company_commission: data?.active_discount?.company_commission || "",
    },
  });

  // Check if business is Premium - check both subscription_package.name and mode
  const isPremium = data?.subscription_package?.name === "Premium" || data?.mode === "Premium";

  // Reset form when data changes
  useEffect(() => {
    if (data) {
      reset({
        discount_text: data?.active_discount?.discount_text || "",
        fixed_discount: data?.active_discount?.fixed_discount || "",
        customer_discount: data?.active_discount?.customer_discount || "",
        company_commission: data?.active_discount?.company_commission || "",
      });
    }
  }, [data, reset]);

  // Watch customer discount and company commission to auto-calculate fixed discount
  const customerDiscount = watch("customer_discount");
  const companyCommission = watch("company_commission");

  useEffect(() => {
    if (isPremium) {
      const customer = parseFloat(customerDiscount) || 0;
      const company = parseFloat(companyCommission) || 0;
      const total = customer + company;
      setValue("fixed_discount", total > 0 ? total : "");
    }
  }, [customerDiscount, companyCommission, isPremium, setValue]);

  const onSubmit = (formData) => {
    // Clean and validate discount data
    const cleanedData = {};
    
    if (formData.discount_text && formData.discount_text.trim() !== '') {
      cleanedData.discount_text = formData.discount_text;
    }
    
    if (formData.fixed_discount !== undefined && formData.fixed_discount !== null && formData.fixed_discount !== '') {
      cleanedData.fixed_discount = formData.fixed_discount;
    }
    
    if (formData.customer_discount !== undefined && formData.customer_discount !== null && formData.customer_discount !== '') {
      cleanedData.customer_discount = formData.customer_discount;
    }
    
    // Only include company_commission if Premium
    if (isPremium && formData.company_commission !== undefined && formData.company_commission !== null && formData.company_commission !== '') {
      cleanedData.company_commission = formData.company_commission;
    }
    
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Discount Information</h3>
        <p className="text-sm text-gray-500 mt-1">Update discount offers and commission rates</p>
      </div>
      
      {/* Discount Text */}
      <div>
        <TextField
          label="Discount Text"
          placeholder="e.g., Flat 10% off on all items"
          maxLength={32}
          {...register("discount_text")}
          error={errors?.discount_text?.message}
        />
      </div>

      {/* Show discount fields only for Premium businesses */}
      {isPremium ? (
        <>
          {/* Discount fields - Premium Mode */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextField
              label="Customer Discount (%)"
              type="number"
              min={0}
              step="0.01"
              placeholder="0"
              {...register("customer_discount", {
                setValueAs: (v) => (v === "" ? null : parseFloat(v)),
              })}
              error={errors?.customer_discount?.message}
            />

            <TextField
              label="Company Commission (%)"
              type="number"
              min={0}
              step="0.01"
              placeholder="0"
              {...register("company_commission", {
                setValueAs: (v) => (v === "" ? null : parseFloat(v)),
              })}
              error={errors?.company_commission?.message}
            />

            <TextField
              label="Fixed Discount (%)"
              type="number"
              min={0}
              step="0.01"
              placeholder="0"
              disabled
              readOnly
              {...register("fixed_discount", {
                setValueAs: (v) => (v === "" ? null : parseFloat(v)),
              })}
              error={errors?.fixed_discount?.message}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            * Fixed Discount is automatically calculated as Customer Discount + Company Commission
          </p>
        </>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Discount percentages are only available for Premium businesses. 
            Upgrade to Premium to access Customer Discount, Company Commission, and Fixed Discount features.
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

// Gallery Edit Modal Form
export const GalleryEditForm = ({ data, onSave, loading = false }) => {
  const [existingImages, setExistingImages] = useState(
    data?.gallery_images || []
  );
  const [newImages, setNewImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);

  const handleRemoveExistingImage = (imageUuid) => {
    setExistingImages((prev) => prev.filter((img) => img.uuid !== imageUuid));
    setImagesToRemove((prev) => [...prev, imageUuid]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate and clean gallery data
    const cleanedData = {
      gallery: newImages || [],
      remove_images: imagesToRemove || [],
    };
    
    console.log('Gallery form submission:', {
      existingImages: existingImages.length,
      newImages: newImages?.length || 0,
      imagesToRemove: imagesToRemove.length,
      cleanedData
    });
    
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Gallery Images</h3>
        <p className="text-sm text-gray-500 mt-1">Manage business gallery photos (max 5 images)</p>
      </div>
      
      {/* Existing Images */}
      {existingImages.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Images
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {existingImages.map((img) => (
              <div key={img.uuid} className="relative group">
                <div className="aspect-square">
                  <img
                    src={img.image}
                    alt="Gallery"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveExistingImage(img.uuid)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Images */}
      {existingImages.length < 5 && (
        <div>
          <ImageDropField
            label={existingImages.length > 0 ? "Add More Images" : "Add New Images"}
            value={newImages}
            onChange={setNewImages}
            hint={`Drop up to ${5 - existingImages.length} images or click to browse`}
            maxFiles={5 - existingImages.length}
          />
        </div>
      )}

      {existingImages.length >= 5 && (
        <div className="text-center py-4 text-gray-500">
          <p>Maximum of 5 images allowed. Remove some existing images to add new ones.</p>
        </div>
      )}

      {/* Show message if no images */}
      {existingImages.length === 0 && (!newImages || newImages.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          <p>No gallery images found. Add some images to showcase your business.</p>
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};


// Business Info Edit Modal Form
export const BusinessInfoEditForm = ({ data, onSave, loading = false }) => {
  console.log('BusinessInfoEditForm received data:', data);
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(businessInfoSchema),
    defaultValues: {
      business_name: data?.business_name || "",
      about_business: data?.about_business || "",
      username: data?.username || "",
      is_trending: Boolean(data?.is_trending === true || data?.is_trending === 1 || data?.is_trending === '1'),
      is_recently_added: Boolean(data?.is_recently_added === true || data?.is_recently_added === 1 || data?.is_recently_added === '1'),
    },
  });

  // Reset form when data changes
  useEffect(() => {
    if (data) {
      reset({
        business_name: data?.business_name || "",
        about_business: data?.about_business || "",
        username: data?.username || "",
        is_trending: Boolean(data?.is_trending === true || data?.is_trending === 1 || data?.is_trending === '1'),
        is_recently_added: Boolean(data?.is_recently_added === true || data?.is_recently_added === 1 || data?.is_recently_added === '1'),
      });
    }
  }, [data, reset]);

  const onSubmit = (formData) => {
    // Clean and validate business info data
    const cleanedData = {
      business_name: formData.business_name,
      about_business: formData.about_business || '',
      username: formData.username,
      is_trending: Boolean(formData.is_trending),
      is_recently_added: Boolean(formData.is_recently_added),
    };
    console.log('Business Info Form Data:', formData);
    console.log('Cleaned Data:', cleanedData);
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Business Information</h3>
        <p className="text-sm text-gray-500 mt-1">Update basic business details and settings</p>
      </div>
      
      {/* Business Name */}
      <div>
        <TextField
          label="Business Name"
          required
          placeholder="Enter business name"
          maxLength={20}
          {...register("business_name")}
          error={errors?.business_name?.message}
        />
      </div>

      {/* Mobile Number (Username) */}
      <div>
        <TextField
          label="Username"
          required
          placeholder="10-digit mobile number"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={10}
          {...register("username")}
          error={errors?.username?.message}
        />
      </div>

      {/* About Business */}
      <div>
        <TextAreaField
          label="About Business"
          rows={3}
          placeholder="Describe your business..."
          {...register("about_business")}
          error={errors?.about_business?.message}
        />
      </div>

      {/* Flags */}
      <div className="space-y-3">
        <div>
          <Controller
            control={control}
            name="is_trending"
            render={({ field }) => (
              <CheckboxField
                label="Add to Trending"
                checked={Boolean(field.value)}
                onChange={(checked) => {
                  console.log('is_trending changed to:', checked);
                  field.onChange(checked);
                }}
              />
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="is_recently_added"
            render={({ field }) => (
              <CheckboxField
                label="Add to Recently Added"
                checked={Boolean(field.value)}
                onChange={(checked) => {
                  console.log('is_recently_added changed to:', checked);
                  field.onChange(checked);
                }}
              />
            )}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

// Business Settings Edit Modal Form
export const BusinessSettingsEditForm = ({ data, onSave, loading = false, subscriptionPackages = [], paymentModes = [], dispatch }) => {
  const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Initialize business timings with 2 slots per day
  const initializeTimings = (businessData) => {
    const existingTimings = businessData?.business_timings || {};
    const timings = {};
    
    allDays.forEach(day => {
      const daySlots = existingTimings[day] || [];
      // Ensure we always have 2 slots
      timings[day] = [
        daySlots[0] || { open: '', close: '' },
        daySlots[1] || { open: '', close: '' }
      ];
    });
    
    return timings;
  };

  // Filter payment modes to only include valid ones from the API
  const filterValidPaymentModes = (modes) => {
    if (!Array.isArray(modes)) return [];
    const validModeNames = paymentModes.map(pm => pm.name);
    return modes.filter(mode => validModeNames.includes(mode));
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(businessSettingsSchema),
    defaultValues: {
      subscription_package_id: data?.subscription_package?.uuid || "",
      payment_modes: filterValidPaymentModes(data?.payment_modes),
      business_timings: initializeTimings(data),
    },
  });

  // Reset form when data changes
  useEffect(() => {
    if (data) {
      reset({
        subscription_package_id: data?.subscription_package?.uuid || "",
        payment_modes: filterValidPaymentModes(data?.payment_modes),
        business_timings: initializeTimings(data),
      });
    }
  }, [data, reset, paymentModes]);

  // Filter only active payment modes
  const activePaymentModes = paymentModes.filter(pm => pm.status);
  const businessTimings = watch('business_timings') || {};

  const onSubmit = (formData) => {
    const cleanedData = {};
    
    // Add subscription package if selected
    if (formData.subscription_package_id && formData.subscription_package_id.trim() !== '') {
      cleanedData.subscription_package_id = formData.subscription_package_id;
    }
    
    // Add payment modes if selected
    if (formData.payment_modes && formData.payment_modes.length > 0) {
      cleanedData.payment_modes = formData.payment_modes;
    }
    
    // Format business timings with shift-wise slots
    const formattedTimings = {};
    allDays.forEach((day) => {
      const daySlots = formData.business_timings[day] || [];
      const validSlots = daySlots.filter(slot => slot.open && slot.close);
      
      if (validSlots.length > 0) {
        formattedTimings[day] = validSlots;
      } else {
        formattedTimings[day] = [];
      }
    });
    
    cleanedData.business_timings = formattedTimings;
    
    console.log('Business Settings Form Data:', formData);
    console.log('Cleaned Data:', cleanedData);
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Business Settings</h3>
        <p className="text-sm text-gray-500 mt-1">Configure subscription, payment modes, and working hours</p>
      </div>
      
      {/* Subscription Package Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Subscription Package
        </label>
        <Controller
          control={control}
          name="subscription_package_id"
          render={({ field }) => (
            <select
              value={field.value}
              onChange={field.onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Subscription Package</option>
              {subscriptionPackages.map((pkg) => (
                <option key={pkg.uuid} value={pkg.uuid}>
                  {pkg.name}
                </option>
              ))}
            </select>
          )}
        />
        {errors?.subscription_package_id && (
          <p className="text-red-500 text-sm mt-1">{errors.subscription_package_id.message}</p>
        )}
      </div>

      {/* Payment Modes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Modes
        </label>
        <Controller
          control={control}
          name="payment_modes"
          defaultValue={[]}
          render={({ field }) => (
            <CheckboxGroupField
              value={field.value}
              onChange={field.onChange}
              options={activePaymentModes.map(pm => ({ label: pm.name, value: pm.name }))}
              error={errors?.payment_modes?.message}
            />
          )}
        />
      </div>

      {/* Business Timings - Shift Wise */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Business Timings (Shift-wise)
        </label>
        <div className="text-xs text-gray-500 mb-3">
          Configure up to 2 shifts per day. Leave empty if closed.
        </div>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {allDays.map((day) => (
            <div key={day} className="border rounded-lg p-3 bg-gray-50">
              <div className="text-sm font-semibold text-gray-700 mb-2 capitalize">
                {day}
              </div>
              
              {/* Slot 1 */}
              <div className="mb-2">
                <div className="text-xs font-medium text-gray-600 mb-1">Slot 1</div>
                <div className="grid grid-cols-2 gap-2">
                  <Controller
                    control={control}
                    name={`business_timings.${day}.0.open`}
                    render={({ field }) => (
                      <TextField
                        type="time"
                        placeholder="Open"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || "")}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`business_timings.${day}.0.close`}
                    render={({ field }) => (
                      <TextField
                        type="time"
                        placeholder="Close"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || "")}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Slot 2 */}
              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">Slot 2</div>
                <div className="grid grid-cols-2 gap-2">
                  <Controller
                    control={control}
                    name={`business_timings.${day}.1.open`}
                    render={({ field }) => (
                      <TextField
                        type="time"
                        placeholder="Open"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || "")}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`business_timings.${day}.1.close`}
                    render={({ field }) => (
                      <TextField
                        type="time"
                        placeholder="Close"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || "")}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        {errors?.business_timings && (
          <p className="text-red-500 text-sm mt-1">{errors.business_timings.message}</p>
        )}
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

// Location Edit Modal Form
export const LocationEditForm = ({ data, onSave, loading = false, states = [], districts = [], talukas = [], dispatch }) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(locationSchema),
    defaultValues: {
      state_id: data?.state?.uuid || "",
      district_id: data?.district?.uuid || "",
      taluka_id: data?.taluka?.uuid || "",
      address: data?.address || "",
      pincode: data?.pincode?.toString() || "",
      business_location_map: data?.business_location_map || data?.business_location_map || "",
      latitude: data?.latitude || null,
      longitude: data?.longitude || null,
    },
  });

  // Reset form when data changes
  useEffect(() => {
    if (data) {
      reset({
        state_id: data?.state?.uuid || "",
        district_id: data?.district?.uuid || "",
        taluka_id: data?.taluka?.uuid || "",
        address: data?.address || "",
        pincode: data?.pincode?.toString() || "",
        business_location_map: data?.business_location_map || data?.business_location_map || "",
        latitude: data?.latitude || null,
        longitude: data?.longitude || null,
      });
    }
  }, [data, reset]);

  const selectedStateId = watch('state_id');
  const selectedDistrictId = watch('district_id');
  const currentLatitude = watch('latitude');
  const currentLongitude = watch('longitude');
  
  // Debug: Log current latitude and longitude values
  useEffect(() => {
    console.log('Current form values - latitude:', currentLatitude, 'longitude:', currentLongitude);
  }, [currentLatitude, currentLongitude]);

  // Helper function to generate Google Maps URL
  const getGoogleMapsUrl = (lat, lng) => {
    return `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  };

  // Handle live location
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log('Got location:', { latitude, longitude });
        const mapUrl = getGoogleMapsUrl(latitude, longitude);
        setValue("business_location_map", mapUrl);
        setValue("latitude", latitude);
        setValue("longitude", longitude);
        console.log('Set values - latitude:', latitude, 'longitude:', longitude);
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Unable to retrieve your location. Please check your browser permissions.");
      }
    );
  };

  const onSubmit = (formData) => {
    console.log('LocationEditForm - Raw form data:', formData);
    
    // Clean and validate location data
    const cleanedData = {};
    
    if (formData.state_id && formData.state_id.trim() !== '') {
      cleanedData.state_id = formData.state_id;
    }
    
    if (formData.district_id && formData.district_id.trim() !== '') {
      cleanedData.district_id = formData.district_id;
    }
    
    if (formData.taluka_id && formData.taluka_id.trim() !== '') {
      cleanedData.taluka_id = formData.taluka_id;
    }
    
    if (formData.address && formData.address.trim() !== '') {
      cleanedData.address = formData.address;
    }
    
    if (formData.pincode && formData.pincode.trim() !== '') {
      cleanedData.pincode = formData.pincode;
    }
    
    if (formData.business_location_map && formData.business_location_map.trim() !== '') {
      cleanedData.business_location_map = formData.business_location_map;
    }
    
    // Include latitude and longitude if available (check for valid numbers)
    if (typeof formData.latitude === 'number' && !isNaN(formData.latitude)) {
      cleanedData.latitude = formData.latitude;
      console.log('✅ Including latitude:', formData.latitude);
    } else {
      console.log('❌ Latitude not included:', formData.latitude, typeof formData.latitude);
    }
    
    if (typeof formData.longitude === 'number' && !isNaN(formData.longitude)) {
      cleanedData.longitude = formData.longitude;
      console.log('✅ Including longitude:', formData.longitude);
    } else {
      console.log('❌ Longitude not included:', formData.longitude, typeof formData.longitude);
    }
    
    console.log('LocationEditForm - Cleaned data:', cleanedData);
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Location Details</h3>
        <p className="text-sm text-gray-500 mt-1">Update business address and location information</p>
      </div>
      
      {/* State Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          State
        </label>
        <Controller
          control={control}
          name="state_id"
          render={({ field }) => (
            <select
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value);
                if (e.target.value && dispatch) {
                  dispatch(fetchDistrictsByState(e.target.value));
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.uuid} value={state.uuid}>
                  {state.name}
                </option>
              ))}
            </select>
          )}
        />
        {errors?.state_id && (
          <p className="text-red-500 text-sm mt-1">{errors.state_id.message}</p>
        )}
      </div>

      {/* District Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          District
        </label>
        <Controller
          control={control}
          name="district_id"
          render={({ field }) => (
            <select
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value);
                if (e.target.value && dispatch) {
                  dispatch(fetchTalukasByDistrict(e.target.value));
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select District</option>
              {districts.map((district) => (
                <option key={district.uuid} value={district.uuid}>
                  {district.name}
                </option>
              ))}
            </select>
          )}
        />
        {errors?.district_id && (
          <p className="text-red-500 text-sm mt-1">{errors.district_id.message}</p>
        )}
      </div>

      {/* Taluka Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Taluka
        </label>
        <Controller
          control={control}
          name="taluka_id"
          render={({ field }) => (
            <select
              value={field.value}
              onChange={field.onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Taluka</option>
              {talukas.map((taluka) => (
                <option key={taluka.uuid} value={taluka.uuid}>
                  {taluka.name}
                </option>
              ))}
            </select>
          )}
        />
        {errors?.taluka_id && (
          <p className="text-red-500 text-sm mt-1">{errors.taluka_id.message}</p>
        )}
      </div>

      {/* Address */}
      <div>
        <TextAreaField
          label="Address"
          rows={3}
          placeholder="Enter full address"
          {...register("address")}
          error={errors?.address?.message}
        />
      </div>

      {/* Pincode */}
      <div>
        <TextField
          label="Pincode"
          placeholder="6-digit pincode"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          {...register("pincode")}
          error={errors?.pincode?.message}
        />
      </div>

      {/* Business Location Map */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Business Location Map URL
          </label>
          <button
            type="button"
            onClick={handleGetLocation}
            className="text-blue-600 text-sm hover:text-blue-800 flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
            Get Current Location
          </button>
        </div>
        <TextField
          placeholder="https://maps.google.com/..."
          {...register("business_location_map")}
          error={errors?.business_location_map?.message}
        />
      </div>

      {/* Hidden fields for latitude and longitude */}
      <input 
        type="hidden" 
        {...register("latitude", {
          setValueAs: (v) => v === "" || v === null || v === undefined ? null : parseFloat(v)
        })} 
      />
      <input 
        type="hidden" 
        {...register("longitude", {
          setValueAs: (v) => v === "" || v === null || v === undefined ? null : parseFloat(v)
        })} 
      />

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

// Categories and Services Edit Modal Form
export const CategoriesServicesEditForm = ({ data, onSave, loading = false, categories = [], subCategories = [], services = [], dispatch }) => {
  console.log('CategoriesServicesEditForm - data:', data);
  console.log('CategoriesServicesEditForm - categories:', categories);
  console.log('CategoriesServicesEditForm - services:', services);
  
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(categoriesServicesSchema),
    defaultValues: {
      category_id: data?.category?.uuid || "",
      sub_categories: data?.subcategories?.map(cat => cat.uuid) || [],
      services: data?.services?.map(service => service.uuid) || [],
    },
  });

  // Reset form when data changes
  useEffect(() => {
    if (data) {
      reset({
        category_id: data?.category?.uuid || "",
        sub_categories: data?.subcategories?.map(cat => cat.uuid) || [],
        services: data?.services?.map(service => service.uuid) || [],
      });
    }
  }, [data, reset]);

  const selectedCategoryId = watch('category_id');
  
  console.log('Current form values - category:', selectedCategoryId);
  
  // Create enriched services options that include current shop services
  const enrichedServices = React.useMemo(() => {
    const serviceMap = new Map();
    
    // Add all fetched services
    services.forEach(service => {
      serviceMap.set(service.uuid, service);
    });
    
    // Add current shop services if they're not already in the list
    if (data?.services) {
      data.services.forEach(service => {
        if (!serviceMap.has(service.uuid)) {
          serviceMap.set(service.uuid, service);
        }
      });
    }
    
    return Array.from(serviceMap.values());
  }, [services, data?.services]);

  const onSubmit = (formData) => {
    console.log('Categories & Services Form Data:', formData);
    console.log('Category ID value:', formData.category_id);
    console.log('Category ID type:', typeof formData.category_id);
    
    // Clean and validate categories and services data
    const cleanedData = {};
    
    // Add category if selected - more robust condition
    if (formData.category_id && String(formData.category_id).trim() !== '') {
      cleanedData.category_id = formData.category_id;
      console.log('✅ Category ID added to cleaned data:', formData.category_id);
    } else {
      console.log('❌ Category ID not added. Value:', formData.category_id);
    }
    
    if (formData.sub_categories && formData.sub_categories.length > 0) {
      cleanedData.sub_categories = formData.sub_categories;
    }
    
    if (formData.services && formData.services.length > 0) {
      cleanedData.services = formData.services;
    }
    
    console.log('Categories & Services Cleaned Data:', cleanedData);
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Categories & Services</h3>
        <p className="text-sm text-gray-500 mt-1">Update business category, subcategories, and services offered</p>
      </div>
      
      {/* Category Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category
        </label>
        <Controller
          control={control}
          name="category_id"
          render={({ field }) => (
            <select
              value={field.value}
              onChange={(e) => {
                const newCategoryId = e.target.value;
                field.onChange(newCategoryId);
                
                // Reset subcategories and services when category changes
                setValue('sub_categories', []);
                setValue('services', []);
                
                if (newCategoryId && dispatch) {
                  const selectedCategory = categories.find(cat => cat.uuid === newCategoryId);
                  if (selectedCategory) {
                    dispatch(fetchSubcategoriesByCategory(selectedCategory.slug));
                    dispatch(fetchServicesByCategory(selectedCategory.slug));
                  }
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category.uuid} value={category.uuid}>
                  {category.name}
                </option>
              ))}
            </select>
          )}
        />
        {errors?.category_id && (
          <p className="text-red-500 text-sm mt-1">{errors.category_id.message}</p>
        )}
      </div>

      {/* Sub Categories */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sub Categories
        </label>
        <Controller
          control={control}
          name="sub_categories"
          render={({ field }) => (
            <UIMultiSelectField
              value={field.value}
              onChange={field.onChange}
              options={subCategories.map((s) => ({
                label: s.name,
                value: s.uuid,
              }))}
              placeholder="Select Sub Categories"
              error={errors?.sub_categories?.message}
              disabled={!subCategories.length}
            />
          )}
        />
      </div>

      {/* Services */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Services
        </label>
        <Controller
          control={control}
          name="services"
          render={({ field }) => (
            <ComboboxField
              value={field.value}
              onChange={field.onChange}
              options={enrichedServices.map((s) => ({
                label: s.name,
                value: s.uuid,
              }))}
              placeholder="Select or type to add services..."
              error={errors?.services?.message}
            />
          )}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};